import { BaseDialog, Button, ClassNamesFromIStyles, ComboBox, useClassNames } from '@h2oai/ui-kit';
import { useMemo, useState } from 'react';

import { useTimeFilters } from './common/hooks';
import { aiUnitWorkspaceColumns } from './components/AIUnitWorkspace/AIUnitWorkspacePage.columns';
import {
  useAIUnitWorkspaceHook,
  useAvailableWorkspaces,
} from './components/AIUnitWorkspace/AIUnitWorkspacePage.services';
import {
  aiUnitWorkspaceStylesDefault,
  buttonIconStyles,
  dialogStyles,
  filterStyles,
  IAIUnitWorkspacePageStyles,
  workspaceComboboxStyles,
} from './components/AIUnitWorkspace/AIUnitWorkspacePage.styles';
import { AIUnitWorkspaceColumns } from './components/AIUnitWorkspace/AIUnitWorkspacePage.utils';
import PodUsageContent from './components/AIUnitWorkspace/PodUsageContent/PodUsageContent';
import DataTable from './components/DataTable/DataTable';
import { dialogContentStyles } from './components/MultiSelect/MultiSelect.styles';
import TimeFilters from './components/TimeFilters/TimeFilters';
import PageWrapper from './PageWrapper';

const AIUnitWorkspace = () => {
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>(['all']);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPodData, setSelectedPodData] = useState<{
    username: string;
    podName: string;
    workspace: string;
    namespace: string;
  } | null>(null);

  const handleAIUnitClick = (username: string, podName: string, workspace: string, namespace: string) => {
    setSelectedPodData({ username, podName, workspace, namespace });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedPodData(null);
  };

  const { startDate, endDate, selectedTimeFrame, handleTimeFilterChange } = useTimeFilters();

  const { events, eventsLoading, workspaceTotals, error } = useAIUnitWorkspaceHook(
    startDate,
    endDate,
    selectedWorkspaces
  );

  const customGroupConfig = [
    {
      fieldName: AIUnitWorkspaceColumns.Workspace,
      titleFormatter: (groupValue: string, _: any[]) => {
        const ws = workspaceTotals.find((wt) => wt.workspaceName === groupValue);
        return `${groupValue} - ${ws?.totalAiUnits} AI Units`;
      },
      styleParentheses: true,
    },
    {
      fieldName: AIUnitWorkspaceColumns.Namespace,
      titleFormatter: (groupValue: string, _: any[], parentKey?: string) => {
        const ws = workspaceTotals.find((wt) => wt.workspaceName === parentKey);
        const ns = ws?.namespaces?.find((ns) => ns.namespaceName === groupValue);
        return `${groupValue} - ${ns?.totalAiUnits} AI Units`;
      },
      styleParentheses: true,
    },
  ];

  const enhancedColumns = useMemo(() => {
    return aiUnitWorkspaceColumns.map((column) => {
      if (column.key === AIUnitWorkspaceColumns.AIUnit) {
        return {
          ...column,
          onRender: (item: any) => (
            <div className={classNames.aiUnitCell}>
              <span>{item[AIUnitWorkspaceColumns.AIUnit]}</span>
              <Button
                iconName="AnalyticsView"
                styles={buttonIconStyles}
                title="View Usage"
                onClick={() =>
                  handleAIUnitClick(
                    item[AIUnitWorkspaceColumns.Username],
                    item[AIUnitWorkspaceColumns.Pod],
                    item[AIUnitWorkspaceColumns.Workspace],
                    item[AIUnitWorkspaceColumns.Namespace]
                  )
                }
              />
            </div>
          ),
          headerTooltip: 'Total AI units used by a user in a specific workspace and a namespace',
        };
      }
      return column;
    });
  }, []);

  const classNames = useClassNames<IAIUnitWorkspacePageStyles, ClassNamesFromIStyles<IAIUnitWorkspacePageStyles>>(
    'user-login-data-page',
    aiUnitWorkspaceStylesDefault
  );

  const { availableWorkspaces, availableWorkspacesLoading, availableWorkspacesError } = useAvailableWorkspaces(
    startDate,
    endDate
  );

  return (
    <PageWrapper>
      <div className={classNames.root}>
        <TimeFilters
          styles={filterStyles()}
          startDate={startDate}
          endDate={endDate}
          selectedTimeFrame={selectedTimeFrame}
          onChange={handleTimeFilterChange}
        />

        <ComboBox
          openOnKeyboardFocus
          label="Select a Workspace"
          multiSelect
          allowFreeform
          autoComplete="on"
          options={[
            { key: 'all', text: 'All Workspaces' },
            ...availableWorkspaces.map((ws) => ({ key: ws, text: ws })),
          ]}
          selectedKey={selectedWorkspaces}
          onChange={(_, option, _index, _value) => {
            if (option) {
              const optionKey = option.key as string;

              if (optionKey === 'all') {
                setSelectedWorkspaces(['all']);
              } else {
                setSelectedWorkspaces((prev) => {
                  const currentSelection = [...prev];

                  if (currentSelection.includes('all')) {
                    return [optionKey];
                  } else {
                    if (currentSelection.includes(optionKey)) {
                      return currentSelection.filter((key) => key !== optionKey);
                    } else {
                      return [...currentSelection, optionKey];
                    }
                  }
                });
              }
            }
          }}
          disabled={availableWorkspacesLoading || !!availableWorkspacesError}
          errorMessage={availableWorkspacesError || ''}
          styles={workspaceComboboxStyles}
        />
        <div className={classNames.dataSectionWrapper}>
          <DataTable
            items={events || []}
            columns={enhancedColumns}
            searchFields={[]}
            defaultGroupedColumns={[AIUnitWorkspaceColumns.Workspace, AIUnitWorkspaceColumns.Namespace]}
            loading={eventsLoading}
            error={!!error}
            contentHeight="calc(100vh - 366px)"
            customGroupConfig={customGroupConfig}
          />
        </div>
        <BaseDialog
          hidden={!isDialogOpen}
          onDismiss={closeDialog}
          dialogContentProps={{
            title: 'AI Unit Usage',
            styles: dialogContentStyles,
          }}
          styles={dialogStyles}
          content={
            <PodUsageContent
              startDate={startDate}
              endDate={endDate}
              podName={selectedPodData ? selectedPodData.podName : ''}
              namespace={selectedPodData ? selectedPodData.namespace : ''}
              onClose={closeDialog}
              username={selectedPodData ? selectedPodData.username : ''}
              workspace={selectedPodData ? selectedPodData.workspace : ''}
            />
          }
        ></BaseDialog>
      </div>
    </PageWrapper>
  );
};

export default AIUnitWorkspace;
